import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@material-ui/data-grid';

type ConversationThread = {
  id: string;
  ticker: string;
  agent: string;
  header: string;
  url: string;
  date: string;
};

export default function ConversationThreads() {
  const [threads, setThreads] = useState<ConversationThread[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let isMounted = true;

    fetch('/profile/conversation_threads')
      .then((resp) => {
        if (!resp.ok) {
          throw new Error('Unable to load conversation threads');
        }
        return resp.json();
      })
      .then((data) => {
        if (isMounted) {
          setThreads(data?.conversation_threads || []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError('Unable to load conversation threads');
          setThreads([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const columns = [
    {field: 'id', headerName: 'Ticker', width: 80, sortable: false, renderHeader:(params) => ( <strong>Ticker</strong>), renderCell: (params) => (params.getValue(params.id, 'ticker') )},
    {field: 'agent', headerName: 'Agent', width: 80, sortable: false, renderHeader:(params) => ( <strong>Agent</strong>)},
    {field: 'header',headerName: 'Subject', width: 300, sortable: true,renderHeader:(params) => ( <strong>Subject</strong>), renderCell: (params) => (<a target="_blank" rel="noopener noreferrer" href={params.getValue(params.id, 'url') }><strong>{params.value}</strong></a>)},
    {field: 'date', headerName: 'Time', width: 180, renderHeader:(params) => ( <strong></strong>)}
  ];

  const filteredThreads = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return threads || [];

    return (threads || []).filter((t) => {
      const searchable = [t.ticker, t.agent, t.header, t.date];
      return searchable.some((field) =>
        (field ?? '').toString().toLowerCase().includes(query)
      );
    });
  }, [threads, searchTerm]);

  if (!threads) {
    return (
      <div className="py-4 border-bottom">
        <p className="clearfix">
          <p><strong>AI Assistant Conversation Threads</strong></p>
          <div>Loading conversation threads...</div>
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 border-bottom">
        <p className="clearfix">
          <p><strong>AI Assistant Conversation Threads</strong></p>
          <div>{error}</div>
        </p>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="py-4 border-bottom">
        <p className="clearfix">
          <p><strong>AI Assistant Conversation Threads</strong></p>
          <div>No conversation threads yet.</div>
        </p>
      </div>
    );
  }

  return (
    <div className="py-4 border-bottom">
      <style>{`
        .grid-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.75rem 0;
        }

        .search-box {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 9999px;
          padding: 0.5rem 1rem 0.5rem 2.75rem;
          transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
          max-width: 40rem;
        }

        .search-box:focus-within {
          border-color: #2563eb;
          box-shadow: 0 10px 25px -10px rgba(37, 99, 235, 0.35);
          background: #ffffff;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          width: 1rem;
          height: 1rem;
          color: #6b7280;
        }

        .search-field {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.95rem;
          color: #111827;
        }

        .search-field::placeholder {
          color: #9ca3af;
        }

        .search-clear {
          background: none;
          border: none;
          color: #6b7280;
          font-weight: 600;
          cursor: pointer;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          transition: background-color 0.15s, color 0.15s;
        }

        .search-clear:hover {
          background-color: #f3f4f6;
          color: #111827;
        }

        .search-meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #4b5563;
          white-space: nowrap;
        }

        .search-meta strong {
          color: #111827;
        }

        .dark-mode .search-box {
          background: #151519;
          border-color: #322f2f;
        }

        .dark-mode .search-box:focus-within {
          border-color: #b66dff;
          box-shadow: 0 10px 25px -10px rgba(182, 109, 255, 0.35);
          background: #222328;
        }

        .dark-mode .search-icon,
        .dark-mode .search-clear,
        .dark-mode .search-field::placeholder,
        .dark-mode .search-meta {
          color: #9c9fa6;
        }

        .dark-mode .search-field,
        .dark-mode .search-meta strong,
        .dark-mode p strong {
          color: #ffffff;
        }

        .dark-mode .search-clear:hover {
          background-color: #1a191d;
          color: #ffffff;
        }
      `}</style>
      <p className="clearfix">
        <p><strong>AI Assistant Conversation Threads</strong></p>
          <div className="grid-toolbar">
            <label className="search-box" htmlFor="conversation-threads-search">
              <svg className="search-icon" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M12.9 14.32a8 8 0 111.414-1.414l4.387 4.387a1 1 0 01-1.414 1.414L12.9 14.32zM14 8a6 6 0 11-12 0 6 6 0 0112 0z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                id="conversation-threads-search"
                className="search-field"
                type="search"
                placeholder="Search tickers, agents, subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  Clear
                </button>
              )}
            </label>
            <div className="search-meta">
              <strong>{filteredThreads.length}</strong>
              of
              <strong>{threads.length}</strong>
              showing
            </div>
          </div>
          <DataGrid
            rows={filteredThreads}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            autoHeight={true}
            autoPageSize={true}
            density={"compact"}
            disableColumnMenu
          />
      </p>
    </div>
  );
}
